import { invoke } from "@tauri-apps/api/core";
import { continuousVoiceActivityHealth } from "./continuous-voice-activity-health";
import { useContinuousVoice } from "./continuous-voice-store";
import {
  isRealUserWords,
  shouldSuppressTtsTranscript,
} from "./continuous-voice-transcript-filter";
import { currentVoiceEngine } from "./denker-agent-voice";
import { detectLiveVoiceControlIntent } from "./continuous-voice-farewell";

/**
 * A reply is paused, not cancelled, when it starts while the microphone
 * segment is open or when sound interrupts it: that sound is often nothing, a
 * cough, Denker's own echo or a short "ok". Real words
 * (`isRealUserWords`) end the reply; anything else resumes it where it
 * stopped. Only Kokoro playback can pause; other engines keep the hard cancel.
 * A hold ends at `HOLD_MAX_MS`, except on Whisper: the limit counts from the
 * last speech the voice-activity detector heard, so talking over the reply
 * keeps it paused up to `HOLD_CEILING_MS`, and once a segment is committed
 * its final decides, bounded by the commit watchdog and the same ceiling. A
 * Whisper hold that runs out cuts the open segment rather than resuming the
 * reply over it, so the user's words are not left waiting behind the reply.
 * Native lets a pause go by itself after six seconds, for a webview that
 * stalled while holding, so every extension renews it, and so does a hold
 * waiting on that final, every `NATIVE_RENEW_MS`.
 */
export const HOLD_MAX_MS = 4_000;
/** However long the user talks over a held reply, it resumes after this. */
export const HOLD_CEILING_MS = 30_000;
/** How often a hold waiting on Whisper's final renews native's pause, well
 *  inside native's own six-second release. */
const NATIVE_RENEW_MS = 4_000;

type ResumeReason = "empty" | "noise" | "echo" | "tiny" | "timeout";

type SpeechHoldHandlers = {
  /** Starts the reply's playback epoch, as an unheld start does, and ends the
   *  segment that held it. */
  resume: (utteranceId: string) => void;
  /** The hard interrupt an unheld start runs: cancel, mute, retire. */
  interrupt: () => void;
  /** Cuts the user's open segment now, through the detector's commit; does
   *  nothing when no segment is open. */
  cutOpenSegment: () => void;
  logDecision: (event: string, props?: Record<string, unknown>) => void;
};

type HeldReply = {
  utteranceId: string;
  heldAt: number;
  timer: ReturnType<typeof setTimeout>;
  /** What the recognizer heard and set aside: echo or a backchannel. */
  discarded: "echo" | "tiny" | null;
  /** An empty segment went to the recognizer, whose final now decides. */
  awaitingFinal: boolean;
};

let handlers: SpeechHoldHandlers | null = null;
let held: HeldReply | null = null;
/** A final-only recognizer sends no partials, so an empty commit can be words. */
let recognizerStreamsPartials = false;
/**
 * The live capture's recognizer, as native names it after any fallback from
 * Whisper: macOS on every ledger event, Windows on the capture's `recording`.
 */
let recognizer: string | null = null;

/** The recognizer a native voice event names. */
export function noteLiveVoiceRecognizer(engine: string | undefined): void {
  if (engine) recognizer = engine;
}

/** Whisper sends no partials: a phrase's words arrive only after its pause. */
export function liveVoiceTranscribesAtPause(): boolean {
  return recognizer === "whisper";
}

export function configureLiveTalkSpeechHold(next: SpeechHoldHandlers): void {
  if (!handlers) {
    let enabled = useContinuousVoice.getState().enabled;
    useContinuousVoice.subscribe((state) => {
      if (state.enabled && !enabled) recognizer = null;
      enabled = state.enabled;
      if (!state.enabled) recognizerStreamsPartials = false;
      if (!state.enabled || state.status === "error") interruptHeld();
    });
  }
  handlers = next;
}

function take(): HeldReply | null {
  const reply = held;
  if (reply) clearTimeout(reply.timer);
  held = null;
  return reply;
}

function heldProps(reply: HeldReply): Record<string, unknown> {
  return {
    utterance: reply.utteranceId,
    held_ms: Date.now() - reply.heldAt,
  };
}

function interruptHeld(): void {
  const reply = take();
  if (!reply) return;
  handlers?.logDecision("speech_hold_cleared", {
    reason: "live_voice_stopped",
    ...heldProps(reply),
  });
  handlers?.interrupt();
}

function confirm(text: string): void {
  const reply = take();
  if (!reply) return;
  handlers?.logDecision("speech_hold_confirmed", {
    ...heldProps(reply),
    transcript_length: text.trim().length,
    control_intent: detectLiveVoiceControlIntent(text.trim())?.intent ?? null,
  });
  handlers?.interrupt();
}

/** Pauses native playback for `reply`, or renews its pause; a failure
 *  cancels the reply as an unheld start would. */
function pauseNativePlayback(reply: HeldReply): void {
  void invoke("denker_agent_voice_pause").catch((error: unknown) => {
    if (held !== reply) return;
    take();
    handlers?.logDecision("speech_hold_failed", {
      utterance: reply.utteranceId,
      error: String(error),
    });
    handlers?.interrupt();
  });
}

/**
 * The hold limit. On Whisper it counts from the last speech heard, up to the
 * ceiling: the user talking over the reply is not a reason to resume it, and
 * their segment's commit and final decide instead. When it runs out with the
 * segment still open (the turn model waiting out an unfinished sentence), the
 * segment is cut so that final decides now.
 */
function holdExpired(reply: HeldReply): void {
  if (held !== reply) return;
  const finalOnly = liveVoiceTranscribesAtPause();
  const lastSpeech = finalOnly
    ? continuousVoiceActivityHealth.lastSpeechAt()
    : null;
  const ceilingAt = reply.heldAt + HOLD_CEILING_MS;
  const expiresAt = Math.min(
    Math.max(reply.heldAt, lastSpeech ?? 0) + HOLD_MAX_MS,
    ceilingAt,
  );
  const remaining = expiresAt - Date.now();
  if (remaining <= 0) {
    // At the ceiling there is no time left to wait for the cut's final.
    if (finalOnly && expiresAt < ceilingAt) {
      handlers?.cutOpenSegment();
      // The cut's commit set the hold awaiting its final, or settled it.
      if (held !== reply || reply.awaitingFinal) return;
    }
    resume("timeout");
    return;
  }
  reply.timer = setTimeout(() => holdExpired(reply), remaining);
  pauseNativePlayback(reply);
  handlers?.logDecision("speech_hold_extended", {
    ...heldProps(reply),
    remaining_ms: remaining,
  });
}

/** Keeps native paused while Whisper's final is due, up to the ceiling. */
function renewWhileAwaitingFinal(reply: HeldReply): void {
  if (held !== reply) return;
  const remaining = reply.heldAt + HOLD_CEILING_MS - Date.now();
  if (remaining <= 0) {
    resume("timeout");
    return;
  }
  pauseNativePlayback(reply);
  reply.timer = setTimeout(
    () => renewWhileAwaitingFinal(reply),
    Math.min(NATIVE_RENEW_MS, remaining),
  );
}

function resume(reason: ResumeReason): void {
  const reply = take();
  if (!reply) return;
  handlers?.logDecision("speech_resumed", { reason, ...heldProps(reply) });
  void invoke("denker_agent_voice_resume").catch((error: unknown) => {
    if (held) return;
    handlers?.logDecision("speech_resume_failed", { error: String(error) });
    handlers?.interrupt();
  });
  handlers?.resume(reply.utteranceId);
}

/** Only Kokoro playback can be paused. */
export function canHoldLiveTalkSpeech(): boolean {
  return currentVoiceEngine() === "kokoro";
}

/**
 * Pauses `utteranceId`: it started over an open segment, or sound interrupted
 * it. False when it cannot be held (no utterance, an engine without pause, or
 * `partialText` already holds real words): the caller cancels it as before.
 */
export function holdLiveTalkSpeech(
  utteranceId: string | null,
  partialText: string,
  reason: "open_segment" | "acoustic",
): boolean {
  if (
    !handlers ||
    !utteranceId ||
    !canHoldLiveTalkSpeech() ||
    isRealUserWords(partialText)
  )
    return false;
  // Native has already replaced a reply held before this one.
  take();
  const reply: HeldReply = {
    utteranceId,
    heldAt: Date.now(),
    timer: setTimeout(() => holdExpired(reply), HOLD_MAX_MS),
    discarded: null,
    awaitingFinal: false,
  };
  held = reply;
  handlers.logDecision("speech_held", {
    reason,
    utterance: utteranceId,
  });
  pauseNativePlayback(reply);
  return true;
}

export function isLiveTalkSpeechHeld(): boolean {
  return held !== null;
}

/** A streamed transcript the echo or backchannel filter set aside. */
export function noteHeldSpeechDiscard(reason: "echo" | "tiny"): void {
  recognizerStreamsPartials = true;
  if (held) held.discarded = reason;
}

/**
 * A streamed transcript that passed the echo filters. True while a reply is
 * held: the hold decides, not playback barge-in. Words end the reply.
 */
export function absorbHeldSpeechPartial(text: string): boolean {
  recognizerStreamsPartials = true;
  if (!held) return false;
  if (isRealUserWords(text)) confirm(text);
  return true;
}

/**
 * The segment's commit while a reply is held. True: it holds no request, so
 * it is not sent and the reply resumes. Words end the reply and are sent.
 */
export function releaseHeldSpeechForCommit(text: string): boolean {
  if (!held) return false;
  const words = text.trim();
  if (isRealUserWords(words)) {
    confirm(words);
    return false;
  }
  // Whisper's final comes after the commit silence and its transcription,
  // often past the hold limit, so the limit no longer applies.
  const finalOnly = liveVoiceTranscribesAtPause();
  if (!words && (finalOnly || !recognizerStreamsPartials)) {
    held.awaitingFinal = true;
    if (finalOnly) {
      clearTimeout(held.timer);
      handlers?.logDecision("speech_hold_awaiting_final", heldProps(held));
      renewWhileAwaitingFinal(held);
    }
    return false;
  }
  resume(words ? "tiny" : (held.discarded ?? "empty"));
  return true;
}

/**
 * A final-only recognizer's answer to the commit above. True: it holds no
 * request, so the caller drops it and the reply resumes.
 */
export function releaseHeldSpeechForFinal(text: string): boolean {
  if (!held?.awaitingFinal) return false;
  const echo = shouldSuppressTtsTranscript(text);
  if (!echo && isRealUserWords(text)) {
    confirm(text);
    return false;
  }
  resume(echo ? "echo" : text.trim() ? "tiny" : "empty");
  return true;
}

/** The segment ended without a transcript worth committing. */
export function resumeHeldSpeech(reason: "empty" | "noise"): void {
  resume(reason);
}

/**
 * Native reported the held utterance over (a newer request replaced it).
 * Resuming clears a pause that landed on that newer request.
 */
export function releaseHeldSpeechOnTerminal(utteranceId: string): void {
  if (held?.utteranceId !== utteranceId) return;
  const reply = take()!;
  handlers?.logDecision("speech_hold_cleared", {
    reason: "terminal",
    ...heldProps(reply),
  });
  void invoke("denker_agent_voice_resume").catch(() => {});
}

/** Any cancel forgets the hold without resuming; native drops its pause. */
export function clearLiveTalkSpeechHold(reason: string): void {
  const reply = take();
  if (!reply) return;
  handlers?.logDecision("speech_hold_cleared", {
    reason,
    ...heldProps(reply),
  });
}
